import { Component, EventEmitter, HostBinding, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import { valueAccessorFactory } from '../../helpers';
import { BaseInputDirective } from '../base-input.directive';
import { formControlFactory } from '../form-control.provider';
import { Formatter } from './formatter';
import { InputSize } from './input-size';
import { InputTypeDirective } from './input-type.directive';
import { InputKeyboardEventMatcher } from './matcher';
import { Parser } from './parser';

export enum Type {
  Text = 'text',
  Password = 'password'
}

export enum Autocomplete {
  ON = 'on',
  OFF = 'off'
}

@Component({
  selector: 'pcs-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  providers: [
    valueAccessorFactory(InputComponent),
    formControlFactory(InputComponent)
  ]
})
export class InputComponent extends BaseInputDirective implements OnChanges {
  @Input() value = '';
  @Input() placeholder = '';
  @Input() helperText: string;
  @Input() type = Type.Text;
  @Input() labelDetails: string;
  @Input() autocomplete = Autocomplete.OFF;
  @Input() size: InputSize = InputSize.Default;
  @Input()
  @HostBinding('class.g-0') @HostBinding('class.row') horizontal = true;
  @Input()
  @HostBinding('class.tooltip-for-value') tooltipForValue = false;
  @Input() labelSize = 6;
  @HostBinding('class')
  @Input() roundedCorners: 'rounded-start' | 'rounded-end';
  @HostBinding('class.label-right') @Input() labelRight;

  @Output() changed = new EventEmitter();
  @Output() blured = new EventEmitter();

  get inputType() {
    return this.inputTypeDirective.inputType;
  }

  private get isLarge() {
    return this.size === InputSize.Large;
  }

  private get isSmall() {
    return this.size === InputSize.Small;
  }

  @HostBinding('class.mb-half')
  private get editAndSmall() {
    return !this.readonly && this.isSmall;
  }

  @HostBinding('class.mb-0')
  private get readOnlyAndSmall() {
    return this.readonly && this.isSmall;
  }

  labelClasses = null;
  inputWrapperClasses = null;
  inputClasses = null;
  modelValue: any;

  private readonly formatter: Formatter;
  private readonly parser: Parser;
  private readonly isKeyAllowed: InputKeyboardEventMatcher;
  private focused = false;

  constructor(private inputTypeDirective: InputTypeDirective) {
    super();
    this.formatter = this.inputTypeDirective.formatter;
    this.parser = this.inputTypeDirective.parser;
    this.isKeyAllowed = this.inputTypeDirective.isKeyAllowed;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.labelClasses = this.calculateLabelClasses();
    this.inputWrapperClasses = this.calculateInputWrapperClasses();
    this.inputClasses = this.calculateInputClasses();
  }

  writeValue(value: any): void {
    this.value = this.formatter(value);
    this.modelValue = this.parser(this.value);
  }

  onInput(event) {
    this.value = event.target.value;
    this.modelValue = this.parser(this.formatter(this.value));
    this.propagateChange(this.modelValue);
    this.changed.emit(this.modelValue);
  }

  onFocus() {
    this.focused = true;
  }

  onBlur(event) {
    this.value = this.formatter(event.target.value);
    this.propagateTouched(event);
    this.blured.emit(event);
    this.focused = false;
  }

  isDefault() {
    return this.size === InputSize.Default;
  }

  onKeyDown(event: KeyboardEvent) {
    return this.isKeyAllowed(event);
  }

  private calculateLabelClasses() {
    return {
      [`col-sm-${this.labelSize} col-form-label horizontal-label`]: this.horizontal,
      'col-form-label-sm': this.isSmall,
      'label-details-icon': !!this.labelDetails
    };
  }

  private calculateInputWrapperClasses() {
    return {
      col: this.horizontal,
      focused: this.focused
    };
  }

  private calculateInputClasses() {
    return {
      '': this.isDefault(),
      'form-control-sm': this.isSmall,
      'form-control-lg': this.isLarge,
      'form-control': !this.readonly,
      'form-control-plaintext': this.readonly
    };
  }

}
